import { Controller } from 'stimulus';
import { FetchRequest } from '@rails/request.js';
import applyNoBgImageChange from '@/js/editor/bg-removal-apply';
import Util from '@/js/editor/util';
import { show as showToast } from '@/js/application_controllers/ui/toast_controller';

const SUPPORTED_KINDS = ['product', 'item'];

export default class extends Controller {
  static targets = ['button', 'errorToast'];
  static values = { url: String, trackUrl: String };
  static classes = ['active', 'loading', 'itemLoading'];

  // `legacy:item-selected` comes from Shared#prepareToolbar and Shared#unselectItems.
  syncState({ detail: { items } }) {
    // multi-select bg removal is not supported yet
    const item = items.length === 1 ? items[0] : null;
    const supported = Boolean(item) && SUPPORTED_KINDS.includes(item.e.kind);

    this.buttonTarget.disabled = !supported;
    this.#setState(
      supported && item.e.backgroundRemovalSource !== 'none',
      supported && Boolean(item.bgRemovalPending)
    );
  }

  toggle() {
    const item = this.#selectedItem;

    if (!item || item.bgRemovalPending || !SUPPORTED_KINDS.includes(item.e.kind)) {
      return;
    }

    if (item.e.backgroundRemovalSource === 'none') {
      this.#enable(item);
    } else {
      this.#disable(item);
    }
  }

  #disable(item) {
    if (item.e.noBgImage) {
      // remember the cutout so toggling back on does not need another request
      item.cachedNoBgImage = item.e.noBgImage;
      item.e.noBgImage = null;
    } else {
      item.upgradedFromLegacy = true;
    }

    item.e.clearLegacyBackgroundRemovalProps();

    this.#commit(item);
    this.#track(item, 'toggled_off');
  }

  async #enable(item) {
    if (item.cachedNoBgImage && item.noBgSourceImage) {
      item.e.noBgImage = item.cachedNoBgImage;
      await this.#commit(item);
      this.#track(item, 'toggled_on', { was_cached: true, triggered_inference: false });
      this.#reflattenInBackground(item);

      return;
    }

    this.#setPending(item, true);

    const request = new FetchRequest('get', this.urlValue, {
      responseKind: 'json',
      query: this.#requestQuery(item),
    });

    let payload;

    try {
      const response = await request.perform();
      if (!response.ok) {
        return this.#failed(item, 'request_failed');
      }
      payload = await response.json;
    } catch {
      return this.#failed(item, 'request_failed');
    }

    const noBgImage = payload.no_bg_image;

    // the item can be deleted while inference runs
    if (!item.classList.contains('item')) {
      return this.#setPending(item, false);
    }

    if (noBgImage.id === 0) {
      return this.#failed(item);
    }

    if (payload.reflattened) {
      item.noBgImageVersion = Date.now();
    }

    // Load the cutout before the item says it has one: crop, undo and redraws paint from
    // `noBgSourceImage` as soon as `noBgImage` is set, and the download can take seconds.
    try {
      await window.ed._shared.getRemovedBgImagesData(item, { noBgImage, temp: item.e.temp });
    } catch {
      item.cachedNoBgImage = null;

      return this.#failed(item, 'image_load_failed');
    }

    item.e.noBgImage = noBgImage;
    item.cachedNoBgImage = noBgImage;

    await this.#commit(item);
    this.#track(item, 'toggled_on', {
      was_cached: payload.was_cached,
      triggered_inference: payload.triggered_inference,
    });
  }

  // A cutout made before flattening existed is flattened when the server reads it, and keeps its
  // id — so the editor would go on painting the see-through copy it already holds. Ask, and redraw
  // only when the bytes really were replaced. For every other cutout the answer changes nothing,
  // which is why this sits behind the toggle instead of in front of it.
  async #reflattenInBackground(item) {
    try {
      const request = new FetchRequest('get', this.urlValue, {
        responseKind: 'json',
        query: this.#requestQuery(item),
      });
      const response = await request.perform();

      if (!response.ok) {
        return;
      }

      const payload = await response.json;

      if (!payload.reflattened || !item.e.noBgImage) {
        return;
      }

      item.noBgImageVersion = Date.now();
      await window.ed._shared.getRemovedBgImagesData(item, item.e);

      // Redrawn without going through #commit: the toggle already recorded itself, and this is the
      // same toggle finishing, not a second one to count or to leave an extra step in the history.
      this.#setPending(item, true);
      await applyNoBgImageChange(window.ed._shared, item);
      this.#setPending(item, false);
    } catch {
      // A cutout left see-through is the status quo, so a failed refresh needs no error path.
    }
  }

  #requestQuery(item) {
    return {
      ...this.#assetQuery(item.e),
      source: 'toolbar_toggle',
      previous_source: item.upgradedFromLegacy ? 'legacy' : 'none',
    };
  }

  // mirrors the request shape the background removal modal uses
  #assetQuery(props) {
    if (props._objProps.imageId === undefined) {
      return { kind_id: props.id, kind: props.kind };
    }

    return props.kind === 'item'
      ? { kind_id: props._objProps.imageId, kind: 'additional_image', item_id: props.id }
      : { kind_id: props._objProps.imageId, kind: 'additional_image', product_id: props.id };
  }

  async #commit(item) {
    this.#setPending(item, true);

    await applyNoBgImageChange(window.ed._shared, item);

    this.#setPending(item, false);

    window.ed.featureActionsService.add(
      item.e.noBgImage
        ? 'left_tool_bar_bg_removed_magic_toggled_on'
        : 'left_tool_bar_bg_removed_magic_toggled_off'
    );

    window.ed._shared.setUnloadConfirmation();
    window.ed._history.setState();
  }

  // No error code means the request itself succeeded and the server already tracked
  // the failure, so we only report the ones it cannot see.
  #failed(item, errorCode = null) {
    window.ed.featureActionsService.add('left_tool_bar_bg_removed_magic_failed');

    this.#setPending(item, false);

    if (errorCode) {
      this.#track(item, 'failed', { error_code: errorCode });
    }

    showToast(this.errorToastTarget);
  }

  #track(item, event, properties = {}) {
    new FetchRequest('post', this.trackUrlValue, {
      responseKind: 'json',
      body: JSON.stringify({ event, ...this.#requestQuery(item), ...properties }),
    })
      .perform()
      .catch(() => {});
  }

  #setPending(item, pending) {
    item.bgRemovalPending = pending;

    this.itemLoadingClasses.forEach((klass) => item.classList.toggle(klass, pending));

    if (item === this.#selectedItem) {
      this.#setState(item.e.backgroundRemovalSource !== 'none', pending);
    }
  }

  #setState(active, loading) {
    this.activeClasses.forEach((klass) => this.buttonTarget.classList.toggle(klass, active));
    this.loadingClasses.forEach((klass) => this.buttonTarget.classList.toggle(klass, loading));
    this.buttonTarget.setAttribute('aria-pressed', String(active));
    this.buttonTarget.setAttribute('aria-busy', String(loading));
  }

  get #selectedItem() {
    return Util.selectedItem();
  }
}
